const DAY_MAP: Record<number, string> = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
}

const buildOpeningHours = (hoursFull: any[]) =>
  hoursFull
    ?.filter(h => h.start !== '24:00:00' && h.end !== '24:00:00')
    .map(h => ({
      '@type': 'OpeningHoursSpecification',
      'dayOfWeek': DAY_MAP[h.day],
      'opens': h.start.slice(0, 5),
      'closes': h.end.slice(0, 5),
    }))

interface ParsedAddress {
  streetAddress?: string
  addressLocality?: string
  postalCode?: string
  addressCountry?: string
}

const parseAddress = (fullAddress: string): ParsedAddress => {
  if (!fullAddress) return {}

  // Split by commas and trim
  const parts = fullAddress.split(',').map(p => p.trim()).filter(Boolean)

  // Simple heuristic for UK addresses
  const postalRegex = /[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}/i

  let postalCode: string | undefined
  let addressLocality: string | undefined
  let streetAddress: string | undefined

  // Try to find postal code in the last part
  const lastPart = parts[parts.length - 1]
  if (postalRegex.test(lastPart)) {
    postalCode = lastPart
    parts.pop()
  }

  // Assume last remaining part is locality/city
  if (parts.length) {
    addressLocality = parts.pop()
  }

  // Remaining parts are street / building / office
  if (parts.length) {
    streetAddress = parts.join(', ')
  }

  return {
    streetAddress,
    addressLocality,
    postalCode,
    addressCountry: 'GB', // default if UK
  }
}

export function buildBusinessSchema(business: Business) {
  const image = getImagePath({ collectionId: business.collectionId, id: business.id, src: business.coverImage })
  const logo = getImagePath({ collectionId: business.collectionId, id: business.id, src: business.logo })
  const parsedAddress = parseAddress(business.map.name || '')
  const route = getPublicRoute('business', business, true)
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': route,
    'name': business.name,
    'description': business.brief || business.description,
    'url': route,
    'image': [image, logo].filter(Boolean),
    'telephone': business.phone,
    'address': parsedAddress.streetAddress || parsedAddress.addressLocality || parsedAddress.postalCode
      ? {
          '@type': 'PostalAddress',
          'streetAddress': parsedAddress.streetAddress,
          'addressLocality': parsedAddress.addressLocality,
          'postalCode': parsedAddress.postalCode,
          'addressCountry': parsedAddress.addressCountry,
        }
      : undefined,

    'sameAs': [
      business.website,
      business.facebook,
      business.instagram,
      business.linkedin,
    ].filter(Boolean),
    'geo': business.map && {
      '@type': 'GeoCoordinates',
      'latitude': business.map.lat,
      'longitude': business.map.lng,
    },
    'openingHoursSpecification': buildOpeningHours(business.hoursFull),
    'aggregateRating': business.averageRating && {
      '@type': 'AggregateRating',
      'ratingValue': business.averageRating,
      'reviewCount': business.reviewCount || 0,
    },
  }
}

export const buildNewsSchema = (news: NewsCollection) => {
  if (news.hidden) return null
  const route = getPublicRoute('news', news, true)
  const image = getImagePath({ collectionId: news.collectionId, id: news.id, src: news.image })
  const schema: any = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    'headline': news.name,
    'description': news.brief || news.description,
    'url': route,
    'datePublished': news.created,
    'dateModified': news.updated || news.created,
    'image': image,
    'author': news.expand?.author
      ? {
          '@type': 'Person',
          'name': news.expand.author.name,
        }
      : undefined,
  }

  // Optional: YouTube video associated with the article
  if (news.youtube) {
    schema.video = {
      '@type': 'VideoObject',
      'name': news.name,
      'contentUrl': news.youtube,
    }
  }

  return schema
}

export const buildEventSchema = (event: EventCollection) => {
  if (event.hidden) return null
  const image = getImagePath({ collectionId: event.collectionId, id: event.id, src: event.image })
  const route = getPublicRoute('event', event, true)
  const schema: any = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    'name': event.name,
    'description': event.description,
    'url': route,
    'startDate': event.dateStart,
    'endDate': event.dateEnd,
    'image': image,
    'eventStatus': 'https://schema.org/EventScheduled',
  }

  // Location
  if (event.map) {
    schema.location = {
      '@type': 'Place',
      'name': event.map.name,
      'geo': {
        '@type': 'GeoCoordinates',
        'latitude': event.map.lat,
        'longitude': event.map.lng,
      },
    }
  }

  // Organizer: If event is linked to a business
  if (event.business && event.expand?.business) {
    const organizerRoute = getPublicRoute('business', event.expand?.business, true)
    schema.organizer = {
      '@type': 'Organization',
      'name': event.expand?.business?.name || 'Organizer',
      'url': organizerRoute,
    }
  }

  return schema
}

export const buildOfferSchema = (offer: OfferCollection) => {
  if (offer.hidden) return null
  const route = getPublicRoute('offer', offer, true)
  const image = getImagePath({ collectionId: offer.collectionId, id: offer.id, src: offer.image })

  const schema: any = {
    '@context': 'https://schema.org',
    '@type': 'Offer',
    'name': offer.name,
    'description': offer.description,
    'url': route,
    'validFrom': offer.dateStart,
    'validThrough': offer.dateEnd,
    'image': image,
  }

  // Location (optional)
  if (offer.map) {
    schema.areaServed = {
      '@type': 'Place',
      'name': offer.map.name,
      'geo': {
        '@type': 'GeoCoordinates',
        'latitude': offer.map.lat,
        'longitude': offer.map.lng,
      },
    }
  }

  // Seller / business (optional)
  if (offer?.expand?.business) {
    const businessRoute = getPublicRoute('business', offer.expand.business, true)
    schema.seller = {
      '@type': 'Organization',
      'name': offer.expand.business.name,
      'url': businessRoute,
    }
  }
  return schema
}

export const buildPageSchema = (page: Page) => {
  const url = getPublicRoute('pages', page, true)
  const image = getImagePath({ collectionId: page.collectionId, id: page.id, src: page.meta_image })
  const schema: any = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': page.name,
    'description': page.meta_description,
    'url': url,
    'image': image,

    // Creator / author
    'creator': {
      '@type': 'Organization',
      'name': 'MCT Web',
      'url': 'https://mctweb.co.uk',
    },
  }

  return schema
}
